import { Card, Grid } from '@design-systems/canvas-ui'
import { ErrorAlert } from 'components/error-fallback'
import { GeneralizedComponent } from 'components/generalized-component/generalized-component'
import { BasicPlaceholder } from 'components/placeholder'
import { useTargetCircle360 } from 'hooks/loyalty/useTargetCircle360'
import useCountdown from 'hooks/utils/useCountdown'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { useCallback, useState } from 'react'
import { isStore } from 'utils/location'
import {
    applyVarianceColorToLastColumn,
    formatChartData,
    setFirstColumnInHeaders,
    updateFirstRowForStore,
} from '../helpers'
import { addHyperlinkToFirstColumn } from 'utils/data'
import { translateTableData } from 'locales/utils'
import useRefresh from 'hooks/utils/useRefresh'
import {
    ResponsiveContainer,
    BarChart,
    CartesianGrid,
    XAxis,
    YAxis,
    Bar,
    Tooltip,
} from 'recharts'

export const TargetCircle360: React.FC = () => {
    const { currentLocation, env, t, logout, session, pathname, isEnglish } =
        useEssentials()
    const { loyalty } = TRANSLATION_KEYS
    const [timePeriod, setTimePeriod] = useState<string>('current_week')
    const [selectedTimeIndex, setSelectedTimeIndex] = useState<number>(0)

    const handleIndexUpdate = useCallback((index: number) => {
        const timePeriods = [
            'current_week',
            'last_week',
            'month_to_date',
            'year_to_date',
        ]
        setTimePeriod(timePeriods[index])
        setSelectedTimeIndex(index)
    }, [])

    let { data, isError, isLoading, error, refetch, remove } =
        useTargetCircle360({
            env,
            accessToken: session?.accessToken ?? '',
            location: currentLocation,
            filters: {
                time_dimension: timePeriod,
                ...(isStore(currentLocation) && {
                    location_id: currentLocation,
                }),
            },
        })
    const count = useCountdown(5, logout, error as Error)

    useRefresh(refetch, remove)
    let processedData = data ? structuredClone(data) : null

    if (processedData) {
        if (isStore(currentLocation)) {
            processedData = updateFirstRowForStore(
                processedData,
                currentLocation,
            )
            processedData.footer = []
        }

        processedData.headings = setFirstColumnInHeaders(
            processedData,
            'location',
        )
        processedData = addHyperlinkToFirstColumn(
            processedData,
            currentLocation,
            pathname,
        )

        processedData = translateTableData({
            t,
            table: processedData,
            translation: loyalty.targetCircle,
        })

        processedData = applyVarianceColorToLastColumn(processedData)
    }
    const payload = {
        'view in %': [
            {
                title: 'week to date',
                subtitle: '',
                data: processedData ? { [currentLocation]: processedData } : {},
            },
            {
                title: 'last week',
                subtitle: '',
                data: processedData ? { [currentLocation]: processedData } : {},
            },
            {
                title: 'month to date',
                subtitle: '',
                data: processedData ? { [currentLocation]: processedData } : {},
            },
            {
                title: 'year to date',
                subtitle: '',
                data: processedData ? { [currentLocation]: processedData } : {},
            },
        ],
    }
    const chartData = formatChartData(processedData)
    interface IChartData {
        signups: number
        forecast: number
    }
    const maxSignups = chartData.length
        ? Math.max(...chartData.map((item: IChartData) => item.signups))
        : 0
    const maxForecast = chartData.length
        ? Math.max(...chartData.map((item: IChartData) => item.forecast))
        : 0
    const yAxisDataKey = maxForecast > maxSignups ? 'forecast' : 'signups'

    if (isLoading) {
        return (
            <div className="placeholder-container" data-testid="placeholder">
                <BasicPlaceholder type="text" rows={1} />
                <BasicPlaceholder type="table" rows={8} columns={3} />
            </div>
        )
    }

    if (isError) {
        return (
            <ErrorAlert error={error} count={count} onRetry={() => refetch()} />
        )
    }
    return (
        <Grid.Container>
            <Grid.Item
                size={{
                    xs: 12,
                }}
            >
                <GeneralizedComponent
                    payload={payload}
                    isLoading={isLoading}
                    scrollableWidth={true}
                    widths={isEnglish ? [90, 90, 90, 100] : [90, 120, 90, 100]}
                    expandedWidth={false}
                    onIndexUpdate={handleIndexUpdate}
                    parentHandleClick={handleIndexUpdate}
                    selectedTimeIndex={selectedTimeIndex}
                    isError={isError}
                    isNoData={!processedData?.rows?.length}
                    titleClassName={'hc8-fs-150'}
                    kpiName="loyalty target circle 360"
                    isTimePeriodSticky={false}
                >
                    <Card>
                        <div data-testid="circle-360-chart">
                            <ResponsiveContainer
                                width="100%"
                                height={240}
                                className={'hc8-mv-6x'}
                            >
                                <BarChart
                                    data={chartData}
                                    {...{
                                        overflow: 'visible',
                                        margin: {
                                            left: 10,
                                            right: 20,
                                            top: 20,
                                            bottom: isStore(currentLocation)
                                                ? 10
                                                : 40,
                                        },
                                    }}
                                    barSize={30}
                                >
                                    <CartesianGrid />
                                    <XAxis
                                        dataKey="name"
                                        angle={
                                            isStore(currentLocation) ? 0 : -90
                                        }
                                        tick={{
                                            textAnchor: isStore(currentLocation)
                                                ? 'middle'
                                                : 'end',
                                        }}
                                        dy={5}
                                        dx={-5}
                                    />

                                    <YAxis
                                        dataKey={yAxisDataKey}
                                        label={{
                                            value: t(
                                                loyalty.targetCircle.signUps,
                                            ),
                                            position: 'center',
                                            angle: -90,
                                            dx: -25,
                                        }}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: 'white',
                                            color: 'black',
                                        }}
                                        itemStyle={{ color: 'black' }}
                                        labelStyle={{ color: 'black' }}
                                    />
                                    <Bar
                                        dataKey="signups"
                                        fill="#512B75"
                                        barSize={15}
                                    />
                                    <Bar
                                        dataKey="forecast"
                                        fill="#808080"
                                        barSize={15}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                </GeneralizedComponent>
            </Grid.Item>
        </Grid.Container>
    )
}
